// src/realtime/conversationSocket.ts
// ==========================================================
//    Conversation Realtime Socket
// ==========================================================

import websocketManager from "../lib/websocketManager";
import type { AppDispatch, RootState } from "../redux/storeTypes";

import {
  bufferDelivery,
  consumeDelivery,
  bufferRead,
  consumeReads,
} from "./conversationDeliveryBuffer";
import {
  addMessage,
  clearFileUploadStatus,
  clearRecordingStatus,
  clearTypingStatus,
  setMessageDelivered,
  setFileUploadStatus,
  setRecordingStatus,
  setTypingStatus,
  updateEditedMessage,
  updateLastMessage,
  updateReadStatus,
  updateMyRole,
  addNewDialogue,
  removeDialogue,
  removeParticipantLocally,
  fetchDialogueBySlug,
  fetchMessageById,
  fetchUnreadCounts,
  resetUnreadCount,
} from "../redux/conversation/conversationSlice";
import { getPrivateKey, getDeviceIdAsync } from "../utils/keyManager";
import {
  decryptMessageLocallyEx,
  type DecryptErr,
} from "../utils/encryptionUtils";
import { logger } from "../utils/logging/logger";

// -------------------------------------------------------------------------------------------------
type DecryptMeta = {
  decrypted_content: string | null;
  decrypt_error_code?: DecryptErr["code"];
  content_encrypted?: string | null;
  encrypted_for_device?: string | null;
};

// -------------------------------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------------------------------
function pickEncryptedForDevice(
  arr: { device_id: string; encrypted_content: string }[] | undefined,
  deviceId: string,
) {
  return (arr || []).find((e) => e.device_id === deviceId);
}

function getActiveDialogueSlugFromUrl(): string | null {
  if (typeof window === "undefined") return null;

  const parts = window.location.pathname.split("/conversation/");
  if (parts.length < 2) return null;

  return parts[1]?.split("/")[0] || null;
}

// -------------------------------------------------------------------------------------------------
// Initialize event handlers
// -------------------------------------------------------------------------------------------------
export const initializeConversationWsEvents = (
  dispatch: AppDispatch,
  getState: () => RootState,
) => {
  const handlers: Record<string, (data: any) => void | Promise<void>> = {};

  // ---------------------------------------------------------------------------
  // typing_status
  // ---------------------------------------------------------------------------
  handlers["typing_status"] = (data) => {
    const state = getState();
    if (data?.sender?.id !== state.auth.user?.id) {
      dispatch(
        setTypingStatus({
          dialogueSlug: data.dialogue_slug,
          sender: data.sender,
          isTyping: data.is_typing,
        }),
      );

      if (!data.is_typing) {
        setTimeout(() => {
          dispatch(clearTypingStatus({ dialogueSlug: data.dialogue_slug }));
        }, 3000);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // recording_status
  // ---------------------------------------------------------------------------
  handlers["recording_status"] = (data) => {
    dispatch(
      setRecordingStatus({
        dialogueSlug: data.dialogue_slug,
        sender: data.sender,
        isRecording: data.is_recording,
        fileType: data.file_type,
      }),
    );

    if (!data.is_recording) {
      setTimeout(() => {
        dispatch(clearRecordingStatus({ dialogueSlug: data.dialogue_slug }));
      }, 3000);
    }
  };

  // ---------------------------------------------------------------------------
  // file_upload_status
  // ---------------------------------------------------------------------------
  handlers["file_upload_status"] = (data) => {
    dispatch(
      setFileUploadStatus({
        dialogueSlug: data.dialogue_slug,
        fileType: data.file_type,
        status: data.status,
        progress: data.progress ?? null,
      }),
    );

    if (data.status === "completed") {
      setTimeout(() => {
        dispatch(clearFileUploadStatus({ dialogueSlug: data.dialogue_slug }));
      }, 2000);
    }
  };

  // ---------------------------------------------------------------------------
  // mark_as_delivered
  // ---------------------------------------------------------------------------
  handlers["mark_as_delivered"] = (data) => {
    const { dialogue_slug, message_id } = data || {};
    if (!dialogue_slug || !message_id) return;

    const state = getState();
    const dialogue = state.conversation.dialogues.find(
      (d) => d.slug === dialogue_slug,
    );

    const exists = dialogue?.messages?.some(
      (m) => String(m.id) === String(message_id),
    );

    // Message not yet in store -> buffer delivery
    if (!exists) {
      bufferDelivery(Number(message_id), dialogue_slug);
      return;
    }

    dispatch(
      setMessageDelivered({
        dialogueSlug: dialogue_slug,
        messageId: Number(message_id),
      }),
    );
  };

  // ---------------------------------------------------------------------------
  // chat_message
  // ---------------------------------------------------------------------------
  handlers["chat_message"] = async (data) => {
    const state = getState();
    const currentUser = state.auth.user?.id;

    if (typeof currentUser !== "number") {
      logger.warn(
        "[conversationSocket] chat_message received before auth user was ready",
        {
          dialogueSlug: data?.dialogue_slug,
          messageId: data?.message_id,
        },
      );
      return;
    }

    const deviceId = await getDeviceIdAsync();
    const senderId = data?.sender?.id ?? null;

    if (!senderId) {
      logger.warn("[conversationSocket] chat_message missing sender", data);
    }

    const isOwnMessage = senderId === currentUser;

    let meta: DecryptMeta = {
      decrypted_content: null,
      content_encrypted: data?.content ?? null,
      encrypted_for_device: data?.encrypted_for_device ?? null,
    };

    if (data?.is_encrypted && data?.content) {
      try {
        if (data.encrypted_for_device === deviceId) {
          const privateKey = await getPrivateKey();
          const result = await decryptMessageLocallyEx(
            data.content,
            privateKey,
          );

          if (result.ok) {
            meta.decrypted_content = result.text;
          } else {
            meta.decrypt_error_code = result.code;
          }
        } else {
          meta.decrypt_error_code = "WRONG_DEVICE";
        }
      } catch (err: any) {
        logger.warn("[conversationSocket] decrypt failed", {
          error: err?.message,
        });
        meta.decrypt_error_code = "UNKNOWN";
      }
    } else {
      meta.decrypted_content = data?.decrypted_content || data?.content || null;
    }

    // Ensure dialogue exists locally
    if (
      !state.conversation.dialogues.some((d) => d.slug === data.dialogue_slug)
    ) {
      await dispatch(fetchDialogueBySlug(data.dialogue_slug));
    }

    // Add message
    dispatch(
      addMessage({
        dialogueSlug: data.dialogue_slug,
        message: {
          ...data,
          id: data.message_id,
          dialogue: data.dialogue_slug,
          sender: data.sender,
          timestamp: data.timestamp,
          isOwnMessage,
          decrypted_content: meta.decrypted_content,
          content_encrypted: meta.content_encrypted,
          encrypted_for_device: meta.encrypted_for_device,
          ...(meta.decrypt_error_code
            ? { decrypt_error_code: meta.decrypt_error_code }
            : {}),
          is_encrypted: data.is_encrypted,
          is_delivered: data.is_delivered,
          is_system: data.is_system,
          system_event: data.system_event,
        } as any,
        currentUserId: currentUser,
      }),
    );

    // Keep last-message in sync
    if (!data?.is_system) {
      dispatch(
        updateLastMessage({
          dialogueSlug: data.dialogue_slug,
          lastMessage: {
            id: data.message_id,
            timestamp: data.timestamp,
            sender: data.sender,
            is_encrypted: data.is_encrypted,
            content_encrypted: data.content ?? null,
            decrypted_content: meta.decrypted_content,
            has_attachment: !!(
              data.image ||
              data.video ||
              data.audio ||
              data.file
            ),
            attachment_type: data.image
              ? "image"
              : data.video
                ? "video"
                : data.audio
                  ? "audio"
                  : data.file
                    ? "file"
                    : undefined,
            ...(meta.decrypt_error_code
              ? { decrypt_error_code: meta.decrypt_error_code }
              : {}),
          } as any,
        }),
      );
    }

    // Apply buffered delivery
    const pendingSlug = consumeDelivery(Number(data.message_id));
    if (pendingSlug) {
      dispatch(
        setMessageDelivered({
          dialogueSlug: pendingSlug,
          messageId: Number(data.message_id),
        }),
      );
    }

    // Apply buffered reads
    const pendingReads = consumeReads(Number(data.message_id));
    if (pendingReads) {
      for (const readerId of pendingReads.readerIds) {
        dispatch(
          updateReadStatus({
            dialogueSlug: pendingReads.dialogueSlug,
            readerId,
            readMessages: [Number(data.message_id)],
          }),
        );
      }
    }

    // Live read if user is viewing this dialogue
    const activeSlug = getActiveDialogueSlugFromUrl();
    if (activeSlug === data.dialogue_slug && senderId !== currentUser) {
      dispatch(resetUnreadCount(data.dialogue_slug));

      websocketManager.sendMessage("conversation", {
        type: "mark_as_read",
        data: {
          dialogue_slug: data.dialogue_slug,
          source: "live_chat",
        },
      });
    }

    // Confirm delivery
    websocketManager.sendMessage("conversation", {
      type: "mark_as_delivered",
      data: {
        dialogue_slug: data.dialogue_slug,
        message_id: data.message_id,
      },
    });
  };

  // ---------------------------------------------------------------------------
  // edit_message
  // ---------------------------------------------------------------------------
  handlers["edit_message"] = async (data) => {
    const currentUser = getState().auth?.user?.id;

    if (!data?.dialogue_slug || !data?.message_id) {
      logger.warn(
        "[conversationSocket] edit_message missing required fields",
        data,
      );
      return;
    }

    const deviceId = await getDeviceIdAsync();

    let newPlain = "";
    let decrypt_error_code: DecryptErr["code"] | undefined;
    let content_encrypted_for_me: string | null = null;

    if (data.is_encrypted && Array.isArray(data.encrypted_contents)) {
      const target = pickEncryptedForDevice(data.encrypted_contents, deviceId);

      if (target) {
        content_encrypted_for_me = target.encrypted_content;

        try {
          const privateKey = await getPrivateKey();
          const result = await decryptMessageLocallyEx(
            target.encrypted_content,
            privateKey,
          );

          if (result.ok) {
            newPlain = result.text;
          } else {
            decrypt_error_code = result.code;
            newPlain = "";
          }
        } catch (err: any) {
          logger.warn("[conversationSocket] decrypt failed on edit", {
            error: err?.message,
          });
          decrypt_error_code = "UNKNOWN";
          newPlain = "";
        }
      } else {
        decrypt_error_code = "WRONG_DEVICE";
        newPlain = "";
      }
    } else {
      newPlain =
        data.decrypted_content ?? data.new_content ?? data.content ?? "";
    }

    dispatch(
      updateEditedMessage({
        dialogue_slug: data.dialogue_slug,
        message_id: data.message_id,
        new_content: newPlain,
        edited_at: data.edited_at,
        is_edited: true,
      }),
    );

    if (decrypt_error_code || content_encrypted_for_me) {
      dispatch({
        type: "conversation/patchMessageMeta",
        payload: {
          dialogueSlug: data.dialogue_slug,
          messageId: data.message_id,
          changes: {
            ...(decrypt_error_code ? { decrypt_error_code } : {}),
            ...(content_encrypted_for_me
              ? {
                  content_encrypted: content_encrypted_for_me,
                  encrypted_for_device: deviceId,
                }
              : {}),
          },
        },
      });
    }

    // Keep last-message preview in sync
    if (!data?.is_system) {
      const state = getState();
      const dialogue = state.conversation.dialogues.find(
        (d) => d.slug === data.dialogue_slug,
      );
      if (dialogue) {
        const last =
          (dialogue as any).last_message ??
          (dialogue as any).lastMessage ??
          null;
        const lastId = last?.id;

        if (String(lastId) === String(data.message_id)) {
          dispatch(
            updateLastMessage({
              dialogueSlug: data.dialogue_slug,
              lastMessage: {
                id: data.message_id,
                timestamp: data.edited_at,
                edited_at: data.edited_at,
                sender: data.sender || { id: currentUser, username: "you" },
                is_encrypted: data.is_encrypted,
                content_encrypted: content_encrypted_for_me,
                decrypted_content: newPlain || null,
                has_attachment: false,
                ...(decrypt_error_code ? { decrypt_error_code } : {}),
              } as any,
            }),
          );
        }
      }
    }
  };

  // ---------------------------------------------------------------------------
  // message_soft_deleted
  // ---------------------------------------------------------------------------
  handlers["message_soft_deleted"] = (data) => {
    dispatch({
      type: "conversation/messageSoftDeleted",
      payload: {
        dialogueSlug: data.dialogue_slug,
        messageId: data.message_id,
        userId: data.user_id,
      },
    });
  };

  // ---------------------------------------------------------------------------
  // message_hard_deleted
  // ---------------------------------------------------------------------------
  handlers["message_hard_deleted"] = (data) => {
    if (!data?.dialogue_slug || !data?.message_id) return;

    dispatch({
      type: "conversation/messageHardDeleted",
      payload: {
        dialogueSlug: data.dialogue_slug,
        messageId: data.message_id,
      },
    });

    // Refresh authoritative dialogue snapshot
    dispatch(fetchDialogueBySlug(data.dialogue_slug));
  };

  // ---------------------------------------------------------------------------
  // trigger_unread_count_update
  // ---------------------------------------------------------------------------
  handlers["trigger_unread_count_update"] = () => {
    dispatch(fetchUnreadCounts());
  };

  // ---------------------------------------------------------------------------
  // mark_as_read
  // ---------------------------------------------------------------------------
  handlers["mark_as_read"] = (data) => {
    const dialogueSlug = data?.dialogue_slug;
    const readerId = data?.reader?.id;
    const readMessages = Array.isArray(data?.read_messages)
      ? data.read_messages
      : [];

    if (!dialogueSlug || !readerId || readMessages.length === 0) {
      return;
    }

    const state = getState();
    const dialogue = state.conversation.dialogues.find(
      (d) => d.slug === dialogueSlug,
    );

    const existingIds = new Set(
      (dialogue?.messages || []).map((m) => Number(m.id)),
    );

    const readyMessageIds: number[] = [];
    const pendingMessageIds: number[] = [];

    for (const messageId of readMessages) {
      if (existingIds.has(Number(messageId))) {
        readyMessageIds.push(Number(messageId));
      } else {
        pendingMessageIds.push(Number(messageId));
      }
    }

    if (readyMessageIds.length > 0) {
      dispatch(
        updateReadStatus({
          dialogueSlug,
          readerId,
          readMessages: readyMessageIds,
        }),
      );
    }

    for (const messageId of pendingMessageIds) {
      bufferRead(Number(messageId), dialogueSlug, Number(readerId));
    }
  };

  // ---------------------------------------------------------------------------
  // file_message
  // ---------------------------------------------------------------------------
  handlers["file_message"] = (data) => {
    const state = getState();
    const currentUser = state.auth.user?.id;

    if (typeof currentUser !== "number") {
      logger.warn(
        "[conversationSocket] file_message received before auth user was ready",
        {
          dialogueSlug: data?.dialogue_slug,
          messageId: data?.message_id,
        },
      );
      return;
    }

    const senderId = data?.sender?.id ?? data?.sender_id ?? null;
    const dialogueSlug = String(data?.dialogue_slug || "").trim();
    const messageId = Number(data?.message_id);

    if (!dialogueSlug || !messageId) return;

    const existingDialogue = state.conversation.dialogues.find(
      (d) => d.slug === dialogueSlug,
    );
    const alreadyInStore = existingDialogue?.messages?.some(
      (m) => String(m.id) === String(messageId),
    );

    if (senderId === currentUser && alreadyInStore) return;
    if (alreadyInStore) return;

    const isEncryptedFile = !!data?.is_encrypted_file;
    const mediaType =
      (data?.file_type as "image" | "video" | "audio" | "file") || "file";
    const url = typeof data?.file_url === "string" ? data.file_url : null;

    const senderObj =
      data?.sender ??
      (senderId
        ? {
            id: senderId,
            username: senderId === currentUser ? "you" : "user",
          }
        : undefined);

    dispatch(
      addMessage({
        dialogueSlug,
        message: {
          id: messageId,
          dialogue: dialogueSlug,
          sender: senderObj,
          timestamp: data?.timestamp,
          isOwnMessage: senderId === currentUser,
          seen_by_users: [],
          is_delivered: false,
          is_encrypted: !!data?.is_encrypted,
          is_encrypted_file: isEncryptedFile,
          decrypted_content: null,
          attachment_type: mediaType,
          file_type: mediaType,
          has_attachment: true,
          ...(url && !isEncryptedFile
            ? {
                file_url: url,
                image_url: mediaType === "image" ? url : undefined,
                video_url: mediaType === "video" ? url : undefined,
                audio_url: mediaType === "audio" ? url : undefined,
              }
            : {}),
          isTemporary: false,
        } as any,
        currentUserId: currentUser,
      }),
    );

    if (!data?.is_system) {
      dispatch(
        updateLastMessage({
          dialogueSlug,
          lastMessage: {
            id: messageId,
            timestamp: data?.timestamp,
            sender: senderObj,
            has_attachment: true,
            attachment_type: mediaType,
            file_type: mediaType,
            is_encrypted: false,
            is_encrypted_file: isEncryptedFile,
            image: mediaType === "image" ? true : undefined,
            video: mediaType === "video" ? true : undefined,
            audio: mediaType === "audio" ? true : undefined,
            file: mediaType === "file" ? true : undefined,
          } as any,
        }),
      );
    }

    const activeSlug = getActiveDialogueSlugFromUrl();
    if (activeSlug === dialogueSlug && senderId !== currentUser) {
      dispatch(resetUnreadCount(dialogueSlug));

      websocketManager.sendMessage("conversation", {
        type: "mark_as_read",
        data: {
          dialogue_slug: dialogueSlug,
          source: "live_file",
        },
      });
    }

    websocketManager.sendMessage("conversation", {
      type: "mark_as_delivered",
      data: {
        dialogue_slug: dialogueSlug,
        message_id: messageId,
      },
    });

    if (!isEncryptedFile) {
      dispatch(fetchMessageById(messageId));
    }
  };

  // ---------------------------------------------------------------------------
  // upload_canceled
  // ---------------------------------------------------------------------------
  handlers["upload_canceled"] = (data) => {
    dispatch(clearFileUploadStatus({ dialogueSlug: data.dialogue_slug }));
  };

  // ---------------------------------------------------------------------------
  // group events
  // ---------------------------------------------------------------------------
  handlers["group_added"] = (data) => {
    if (!data?.dialogue?.slug) return;
    dispatch(addNewDialogue(data.dialogue));
  };

  handlers["group_removed"] = (data) => {
    const slug = data?.dialogue?.slug;
    if (!slug) return;
    dispatch(removeDialogue(slug));
  };

  handlers["group_left"] = (data) => {
    if (!data?.dialogue_slug || !data?.user?.id) return;

    dispatch(
      removeParticipantLocally({
        dialogueSlug: data.dialogue_slug,
        userId: data.user.id,
      }),
    );
  };

  handlers["founder_transferred"] = (data) => {
    if (!data?.dialogue_slug || !data?.new_founder_id) return;

    const state = getState();
    const currentUserId = state.auth.user?.id;

    if (currentUserId === data.new_founder_id) {
      dispatch(
        updateMyRole({
          dialogueSlug: data.dialogue_slug,
          newRole: "founder",
        }),
      );
    }

    const dialogue = state.conversation.dialogues.find(
      (d) => d.slug === data.dialogue_slug,
    );

    if (
      dialogue?.my_role === "founder" &&
      currentUserId !== data.new_founder_id
    ) {
      dispatch(
        updateMyRole({
          dialogueSlug: data.dialogue_slug,
          newRole: "participant",
        }),
      );
    }
  };

  // ---------------------------------------------------------------------------
  // unread_count_update
  // ---------------------------------------------------------------------------
  handlers["unread_count_update"] = (data) => {
    // Supports:
    // 1) snapshot: { mode: "snapshot", payload: [...] }
    // 2) incremental: { payload: [...] }
    // 3) direct array snapshot
    const isArrayPayload = Array.isArray(data);
    const mode = isArrayPayload ? "snapshot" : (data?.mode ?? "incremental");
    const results = isArrayPayload ? data : data?.payload;

    if (!Array.isArray(results)) return;

    const state = getState();
    const currentUserId = state.auth?.user?.id;
    const activeSlug = getActiveDialogueSlugFromUrl();

    results.forEach(({ dialogue_slug, unread_count, sender_id }) => {
      if (!dialogue_slug) return;

      // Never count current active dialogue as unread
      if (activeSlug === dialogue_slug) {
        dispatch(resetUnreadCount(dialogue_slug));
        return;
      }

      if (mode === "snapshot") {
        dispatch({
          type: "conversation/updateUnreadCount",
          payload: {
            dialogueSlug: dialogue_slug,
            count: typeof unread_count === "number" ? unread_count : 0,
          },
        });
        return;
      }

      if (sender_id === currentUserId) return;

      const currentCount =
        state.conversation.unreadCounts?.[dialogue_slug] ?? 0;
      const delta =
        typeof unread_count === "number" && unread_count > 0 ? unread_count : 1;

      dispatch({
        type: "conversation/updateUnreadCount",
        payload: {
          dialogueSlug: dialogue_slug,
          count: currentCount + delta,
        },
      });
    });
  };

  // ---------------------------------------------------------------------------
  // conversation:error
  // ---------------------------------------------------------------------------
  handlers["error"] = (data) => {
    logger.warn("[conversationSocket] conversation error", data);
  };

  // Register all handlers
  Object.entries(handlers).forEach(([eventType, callback]) => {
    websocketManager.on(`conversation:${eventType}`, callback);
  });

  // Cleanup unregister
  return () => {
    Object.entries(handlers).forEach(([eventType, callback]) => {
      websocketManager.off(`conversation:${eventType}`, callback);
    });
  };
};

// -------------------------------------------------------------------------------------------------
// Setup
// -------------------------------------------------------------------------------------------------
export const setupConversationWebSocket =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      logger.warn(
        "[conversationSocket] No token; blocking websocket connection",
      );
      return;
    }

    if (!websocketManager.isConnected()) {
      websocketManager.connect(token, "conversation");
    }

    if (!websocketManager.hasEventsRegistered("conversation")) {
      initializeConversationWsEvents(dispatch, getState);
      websocketManager.markEventsAsRegistered("conversation");
    }
  };

// -------------------------------------------------------------------------------------------------
// Cleanup
// -------------------------------------------------------------------------------------------------
export const cleanupConversationWebSocket = (_dispatch: AppDispatch) => {
  if (websocketManager.isConnected()) {
    websocketManager.disconnectAll();
  }
};
